// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { Formik } from 'formik';
import CableReserveField from 'esm-web-pro/containers/pages/network/forms/fields/CableReserveField';
import { isEmpty } from 'lodash';
import { NumberInput } from 'speed-js-react-web';
import Select from 'speed-js-react-web/src/components/grommet2/forms/fields/Select';
import * as Yup from 'yup';
import CableLine from 'esm-web-pro/containers/pages/network/objects/cable/CableLine';
import type { Lines, Points } from 'esm-web-pro/containers/pages/network/objects/cable/create/CreateCable';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import CablePathProviderRef from 'esm-web-pro/containers/pages/network/forms/fields/CablePathProviderRef';
import { useGetShadowBottom, StyledFormBox, StyledActionBox } from 'esm-web-pro/containers/forms/ApiForm';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import CableEitExport from 'esm-web-pro/containers/pages/network/objects/cable/create/CableEITExport';
import { FormBox } from 'speed-js-react-web/src/components/grommet2/forms/GrommetForm';
import RemoveRowButton from './RemoveRowButton';


type Props = {
    actions?: Object,
    height: string,
    padding?: string | Object,
    overflow: String,
    points: Points,
    lines: Lines,
    onLastRemove?: () => void,
    onLineChange?: (index: number, choice: string) => void,
    onReserveChange: (index: number, reserve: number) => void,
    onProviderRefChange: (index: number, providerRef: number) => void,
    disableSave?: boolean,
    onClose: Function,
    onSave: Function,
    provider: Object,
    initialExport: boolean,
    children: React.Node,
    isSubmitting: boolean,
};

const CableForm = (props: Props) => {
    const {
        points, lines, onLastRemove, onLineChange, onReserveChange, onProviderRefChange,
        disableSave, onClose, onSave, provider, initialExport,
        overflow, padding, height, children, isSubmitting,
    } = props;
    const formRef: { current: null | React.ElementRef<Formik> } = React.useRef(null);
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [eitExport, setEitExport] = React.useState(initialExport);
    const [grommetFormBoxRef, setGrommetFormBoxRef] = React.useState(null);

    const handleFormChange = React.useCallback(() => {
        if (formRef.current) {
            formRef.current.getFormikBag().validateForm().then(() => {
                if (formRef.current) {
                    setFormIsValid(isEmpty(formRef.current.getFormikBag().errors));
                } else {
                    setFormIsValid(false);
                }
            });
        } else {
            setFormIsValid(false);
        }
    }, []);

    const handleClose = React.useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSave = React.useCallback(() => {
        onSave({ export: eitExport });
    }, [onSave, eitExport]);

    const handleReserveChange = React.useCallback((index, reserve) => {
        onReserveChange(index, reserve);
    }, [onReserveChange]);

    const handleLineChange = React.useCallback((index, choice) => {
        onLineChange(index, choice);
    }, [onLineChange]);

    React.useEffect(() => {
        handleFormChange();
    }, [lines, points, handleFormChange]);

    const filteredPoints = React.useMemo(() => (
        points.filter((p) => !p.continuousPipe && p.pointNumber)
    ), [points]);
    const filteredLines = React.useMemo(() => (
        lines.filter((l) => !l.continuousPipe && l.options)
    ), [lines]);

    const pointsInitials = React.useMemo(() => {
        const _pointsInitials = {};
        filteredPoints.forEach((p) => {
            _pointsInitials[p.pointNumber] = p.reserve || '0';
        });
        return _pointsInitials;
    }, [filteredPoints]);

    const [shadow, getShadowBottom] = useGetShadowBottom();

    // See if shadow bottom is enabled when components mounts for the first time and when the form updates.
    React.useEffect(() => {
        getShadowBottom();
    }, [grommetFormBoxRef, getShadowBottom]);

    const boxRef = React.useRef();

    const { linesInitials, providerRefInitials } = React.useMemo(() => {
        const _linesInitials = {};
        const _providerRefInitials = {};

        filteredLines.forEach((line) => {
            _linesInitials[`${line.startNumber}-${line.startNumber + 1}`] = line.value || null;
            _providerRefInitials[`${line.startNumber}-provider-ref`] = line.providerRef || null;
        });

        return {
            linesInitials: _linesInitials,
            providerRefInitials: _providerRefInitials,
        };
    }, [filteredLines]);

    const validationSchema = React.useMemo(() => {
        const validationShape = {};
        filteredPoints.forEach((point: Object) => {
            validationShape[point.pointNumber?.toString()] = NumberInput.getValidationSchema(
                { validation: { min_value: 0, required: true } },
            );
        });
        filteredLines.forEach((line: Object) => {
            validationShape[`${line.startNumber}-${line.startNumber + 1}`] = Select.getValidationSchema(
                { validation: { required: true } },
            );
        });
        return Yup.object().shape(validationShape);
    }, [filteredPoints, filteredLines]);

    const formInitial = React.useMemo(() => ({
        ...pointsInitials, ...linesInitials, ...providerRefInitials,
    }), [pointsInitials, linesInitials, providerRefInitials]);
    return (
        <StyledFormBox ref={boxRef} overflow={overflow || 'auto'} onScroll={getShadowBottom}>
            <Box pad={padding} height="100%">
                <Formik
                    {...props}
                    key={`${filteredPoints.length}-${filteredLines.length}`}
                    ref={formRef}
                    validationSchema={validationSchema}
                    validateOnChange
                    initialValues={formInitial}
                    onSubmit={undefined}
                    getGrommetFormBoxRef={setGrommetFormBoxRef}
                    onError={() => isSubmitting(false)}
                >
                    <FormBox>
                        {children}
                        <CableEitExport
                            value={eitExport}
                            onEitExportChange={setEitExport}
                            provider={provider}
                            lines={lines}
                        />
                        {filteredPoints.map<React.Node>((point: Object, index: number) => (
                            <Box key={point.pointNumber} flex={{ shrink: 0 }}>
                                <Box key={point.pointNumber} direction="row">
                                    <Box flex={{ grow: 1 }}>
                                        <CableReserveField
                                            pointKey={point.pointNumber.toString()}
                                            onChange={(reserve) => handleReserveChange(points
                                                .indexOf(filteredPoints[index]), reserve)}
                                            number={point.pointNumber}
                                            value={point.reserve}
                                        />
                                    </Box>
                                    {(onLastRemove && index === filteredPoints.length - 1) && (
                                        <RemoveRowButton onClick={() => onLastRemove()} key={point.pointNumber} />
                                    )}
                                </Box>
                                {(index < filteredLines.length && filteredLines[index].options) && (
                                    <>
                                        <CablePathProviderRef
                                            line={filteredLines[index]}
                                            onChange={(providerRef) => onProviderRefChange(
                                                lines.indexOf(filteredLines[index]), providerRef,
                                            )}
                                        />
                                        <CableLine
                                            line={filteredLines[index]}
                                            onChange={(choice) => handleLineChange(lines
                                                .indexOf(filteredLines[index]), choice)}
                                            disabled={!onLineChange}
                                        />
                                    </>
                                )}
                            </Box>
                        ))}
                        <StyledActionBox shadow={shadow}>
                            <Spinner loading={isSubmitting} />
                            <ActionButtons
                                disabled={isEmpty(points)
                                    || isEmpty(lines) || !formIsValid || disableSave || isSubmitting}
                                onSave={handleSave}
                                onClose={handleClose}
                            />
                        </StyledActionBox>
                    </FormBox>
                </Formik>
            </Box>
        </StyledFormBox>
    );
};

CableForm.defaultProps = {
    disableSave: false,
    onLastRemove: undefined,
    onLineChange: undefined,
    initialExport: false,
    height: null,
    actions: null,
    padding: { bottom: '60px', horizontal: 'small' },
};

export default CableForm;
